import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '@immo/ui-kit';

const InsideMainContext = createContext(false);

/** Marks its subtree as already inside the page's one `<main>` — the app shell wraps its outlet in it. */
export function MainLandmarkProvider({ children }: { readonly children: ReactNode }) {
  return <InsideMainContext.Provider value>{children}</InsideMainContext.Provider>;
}

/**
 * The outer element of a full-page state (error, not-found, outdated). Rendered on its own it is the
 * page's `<main>` and fills the screen; rendered inside the shell's `<main>` it becomes a `<section>`
 * that fills the content pane, so the page keeps exactly one main landmark and does not scroll by the
 * topbar's height.
 */
export function PageStateLandmark({
  className,
  children,
}: {
  readonly className: string;
  readonly children: ReactNode;
}) {
  const inside = useContext(InsideMainContext);

  return inside ? (
    <section className={cn(className, 'min-h-full')}>{children}</section>
  ) : (
    <main className={cn(className, 'min-h-screen')}>{children}</main>
  );
}
